import { useState } from 'react';
import { IFormSection, IFormColumn } from '@/interfaces/form.model';
import FieldConfigModal from './FieldConfigModal';
import ConfirmModal from '@/components/Modal/ConfirmModal';

interface IDraggableFieldListProps {
  fields: IFormColumn[];
  onUpdateFields: (orderedFields: IFormColumn[]) => void;
  onEditField: (field: IFormColumn) => void;
  onDeleteField: (fieldId: string) => void;
  isViewMode?: boolean;
}

const DraggableFieldList: React.FC<IDraggableFieldListProps> = ({
  fields,
  onUpdateFields,
  onEditField,
  onDeleteField,
  isViewMode,
}) => {
  const [draggedIdx, setDraggedIdx] = useState<number | null>(null);

  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    index: number,
  ) => {
    if (isViewMode) return;
    setDraggedIdx(index);
    // Needed for Firefox
    e.dataTransfer.setData('text/plain', index.toString());
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (isViewMode) return;
    e.preventDefault(); // Necessary to allow dropping
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (
    e: React.DragEvent<HTMLDivElement>,
    targetIndex: number,
  ) => {
    if (isViewMode) return;
    e.preventDefault();
    if (draggedIdx === null || draggedIdx === targetIndex) return;

    const newFields = [...fields];
    // Remove the dragged item
    const [draggedItem] = newFields.splice(draggedIdx, 1);
    // Insert it at the target
    newFields.splice(targetIndex, 0, draggedItem);

    // Update sequences
    const reorderedFields = newFields.map((f, i) => ({
      ...f,
      sequence: i + 1,
    }));
    onUpdateFields(reorderedFields);
    setDraggedIdx(null);
  };

  return (
    <div className="d-flex flex-column gap-2">
      {fields.length === 0 ? (
        <div className="text-center p-4 border rounded bg-light text-muted">
          No fields added yet. Click "Add Field" to begin.
        </div>
      ) : (
        fields.map((field, index) => (
          <div
            key={field.id}
            className={`d-flex align-items-center justify-content-between p-3 border rounded bg-white shadow-sm transition-all ${
              draggedIdx === index ? 'opacity-50 border-primary' : ''
            }`}
            draggable={!isViewMode}
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e)}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={() => setDraggedIdx(null)}
            style={{ cursor: isViewMode ? 'default' : 'grab' }}
          >
            <div className="d-flex align-items-center gap-3">
              {!isViewMode && (
                <div className="text-muted" style={{ cursor: 'grab' }}>
                  <i className="bi bi-grip-vertical fs-5"></i>
                </div>
              )}
              <div>
                <div className="d-flex align-items-center gap-2">
                  <span className="fw-semibold">{field.label}</span>
                  {field.required && <span className="text-danger">*</span>}
                  <span className="badge bg-light text-dark border">
                    {field.columnDatatype}
                  </span>
                </div>
                <div className="text-muted small mt-1">
                  ID: {field.columnName}
                </div>
              </div>
            </div>

            {!isViewMode && (
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary p-1 px-2"
                  onClick={() => onEditField(field)}
                >
                  <i className="bi bi-pencil"></i>
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger p-1 px-2"
                  onClick={() => field.id && onDeleteField(field.id)}
                >
                  <i className="bi bi-trash"></i>
                </button>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
};

interface ISectionManagerProps {
  sections: IFormSection[];
  onUpdateSections: (sections: IFormSection[]) => void;
  isViewMode?: boolean;
}

const SectionManager: React.FC<ISectionManagerProps> = ({
  sections,
  onUpdateSections,
  isViewMode,
}) => {
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [configModal, setConfigModal] = useState<{
    isOpen: boolean;
    data?: IFormColumn;
  }>({
    isOpen: false,
  });
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    type: 'section' | 'field';
    id: string;
  }>({
    isOpen: false,
    type: 'section',
    id: '',
  });

  const handleAddSection = () => {
    const newSection: IFormSection = {
      id: Math.random().toString(),
      name: `Section ${sections.length + 1}`,
      sequence: sections.length + 1,
      columns: [],
    };
    onUpdateSections([...sections, newSection]);
    setActiveSectionId(newSection.id!);
  };

  const handleUpdateSectionName = (id: string, name: string) => {
    const updated = sections.map((section) =>
      section.id === id ? { ...section, name } : section,
    );
    onUpdateSections(updated);
  };

  const handleDeleteSection = () => {
    const updated = sections.filter((section) => section.id !== deleteModal.id);
    onUpdateSections(updated);
    setDeleteModal({ isOpen: false, type: 'section', id: '' });
    if (activeSectionId === deleteModal.id) {
      setActiveSectionId(null);
    }
  };

  const activeSection = sections.find((s) => s.id === activeSectionId);

  const handleSaveField = (field: IFormColumn) => {
    if (!activeSectionId) return;

    const updatedSections = sections.map((section) => {
      if (section.id !== activeSectionId) return section;

      const fieldExists = section.columns.some(
        (column) => column.id === field.id,
      );
      let newColumns;

      if (fieldExists) {
        newColumns = section.columns.map((column) =>
          column.id === field.id ? field : column,
        );
      } else {
        newColumns = [...section.columns, field];
      }

      return { ...section, columns: newColumns };
    });

    onUpdateSections(updatedSections);
  };

  const handleDeleteField = () => {
    if (!activeSectionId) return;

    const updatedSections = sections.map((s) => {
      if (s.id !== activeSectionId) return s;
      const newColumns = s.columns.filter(
        (column) => column.id !== deleteModal.id,
      );
      return { ...s, columns: newColumns };
    });

    onUpdateSections(updatedSections);
    setDeleteModal({ isOpen: false, type: 'field', id: '' });
  };

  const handleUpdateFieldsOrder = (orderedFields: IFormColumn[]) => {
    const updatedSections = sections.map((field) => {
      if (field.id !== activeSectionId) return field;
      return { ...field, columns: orderedFields };
    });
    onUpdateSections(updatedSections);
  };

  return (
    <div className="card shadow-sm border-0 mb-4">
      <div className="card-body p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h5 className="card-title mb-0">Step 2: Sections & Fields</h5>
          {!isViewMode && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleAddSection}
            >
              <i className="bi bi-plus-lg me-2"></i> Add Section
            </button>
          )}
        </div>

        {sections.length === 0 ? (
          <div className="text-center py-5 bg-light rounded text-muted">
            <i className="bi bi-layout-text-window-reverse fs-1 mb-3 d-block"></i>
            <p className="mb-0">
              No sections added. Build your form by adding a section first.
            </p>
          </div>
        ) : (
          <div className="d-flex flex-column gap-3">
            {sections.map((section) => (
              <div key={section.id} className="border rounded">
                {/* Section Header */}
                <div
                  className={`d-flex justify-content-between align-items-center p-3 ${activeSectionId === section.id ? 'bg-primary-subtle border-bottom border-primary border-opacity-25' : 'bg-light'}`}
                  style={{ cursor: 'pointer' }}
                >
                  <div
                    className="d-flex align-items-center gap-3 flex-grow-1"
                    onClick={() =>
                      setActiveSectionId(
                        activeSectionId === section.id ? null : section.id!,
                      )
                    }
                  >
                    <i
                      className={`bi ${activeSectionId === section.id ? 'bi-chevron-down' : 'bi-chevron-right'} text-primary`}
                    ></i>
                    {activeSectionId === section.id && !isViewMode ? (
                      <input
                        type="text"
                        className="form-control form-control-sm w-50"
                        value={section.name}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) =>
                          handleUpdateSectionName(section.id!, e.target.value)
                        }
                      />
                    ) : (
                      <h6 className="mb-0">{section.name}</h6>
                    )}
                    <span className="badge bg-secondary rounded-pill ms-2">
                      {section.columns.length} fields
                    </span>
                  </div>

                  {!isViewMode && (
                    <div className="d-flex gap-2 ms-3">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteModal({
                            isOpen: true,
                            type: 'section',
                            id: section.id!,
                          });
                        }}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  )}
                </div>

                {/* Section Body (Fields) */}
                {activeSectionId === section.id && (
                  <div className="p-3 bg-white bg-opacity-50">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0 text-muted">Fields</h6>
                      {!isViewMode && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() =>
                            setConfigModal({ isOpen: true, data: undefined })
                          }
                        >
                          <i className="bi bi-plus-lg me-1"></i> Add Field
                        </button>
                      )}
                    </div>

                    <DraggableFieldList
                      fields={section.columns}
                      onUpdateFields={handleUpdateFieldsOrder}
                      onEditField={(field) =>
                        setConfigModal({ isOpen: true, data: field })
                      }
                      onDeleteField={(id) =>
                        setDeleteModal({ isOpen: true, type: 'field', id })
                      }
                      isViewMode={isViewMode}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <FieldConfigModal
        isOpen={configModal.isOpen}
        toggle={() => setConfigModal({ isOpen: false })}
        onSave={handleSaveField}
        initialData={configModal.data}
        sequence={(activeSection?.columns.length || 0) + 1}
      />

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        toggle={() => setDeleteModal({ ...deleteModal, isOpen: false })}
        submitHandler={
          deleteModal.type === 'section'
            ? handleDeleteSection
            : handleDeleteField
        }
      />
    </div>
  );
};

export default SectionManager;
